// src/features/billing/CreditGrantsCard.tsx
// Interactive credit-grant management for a billing account. Embedded in
// BillingAccountDetailPage next to Payment Methods — "grant credit" and
// "void a grant with a reason" are billing-admin actions performed while
// looking at a specific account.
//
// Covers: ListCreditGrants, CreateCreditGrant, VoidCreditGrant.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useBillingCredit, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import type { CreditGrant } from '@core/types/billing';
import { formatMoney, formatDate } from './format';
import { GrantCreditModal, type GrantCreditValue } from './GrantCreditModal';
import { VoidCreditGrantModal } from './VoidCreditGrantModal';

export interface CreditGrantsCardProps {
  billingAccountId: string;
}

export const CreditGrantsCard = observer(({ billingAccountId }: CreditGrantsCardProps) => {
  const store = useBillingCredit();
  const { t } = useI18n();
  const [grantOpen, setGrantOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState<CreditGrant | null>(null);

  useEffect(() => {
    if (billingAccountId) store.ListCreditGrants(billingAccountId).catch(() => {});
  }, [billingAccountId]);

  const handleGrant = async (value: GrantCreditValue) => {
    await store.CreateCreditGrant(billingAccountId, {
      entity: {} as any,
      billing_account: { id: billingAccountId } as any,
      amount: {
        amount: Math.round(Number(value.amount) * 100),
        currency: `CURRENCY_${value.currency.toUpperCase()}` as any,
        decimal_places: 2,
      } as any,
      reason: value.reason,
      expires_at: value.expiresAt ? new Date(value.expiresAt).toISOString() as any : undefined,
      metadata: {},
    } as any);
    setGrantOpen(false);
  };

  const handleVoid = async (reason: string) => {
    if (!voidTarget?.entity?.id) return;
    await store.VoidCreditGrant(voidTarget.entity.id, { reason } as any);
    setVoidTarget(null);
    await store.ListCreditGrants(billingAccountId);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{t('billing.creditGrants.title')}</CardTitle>
        <Button size="sm" onClick={() => setGrantOpen(true)}>{t('billing.creditGrants.grant')}</Button>
      </CardHeader>
      <CardContent>
        {store.error ? (
          <div className="mb-4"><ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /></div>
        ) : null}

        {store.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.creditGrants.amount')}</TableHead>
                <TableHead>{t('billing.creditGrants.remaining')}</TableHead>
                <TableHead>{t('billing.creditGrants.reason')}</TableHead>
                <TableHead>{t('billing.creditGrants.expiresAt')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {store.items.length === 0 ? (
                <TableEmpty colSpan={6} message={t('billing.creditGrants.empty')} />
              ) : (
                store.items.map((cg) => (
                  <TableRow key={cg.entity?.id}>
                    <TableCell>{formatMoney(cg.amount)}</TableCell>
                    <TableCell>{formatMoney(cg.remaining)}</TableCell>
                    <TableCell>{cg.reason || '--'}</TableCell>
                    <TableCell>{formatDate(cg.expires_at as any)}</TableCell>
                    <TableCell>
                      {cg.is_expired ? (
                        <Badge variant="secondary" size="sm">{t('billing.creditGrants.expired')}</Badge>
                      ) : cg.is_active ? (
                        <Badge variant="success" size="sm">{t('common.active')}</Badge>
                      ) : (
                        <Badge variant="secondary" size="sm">{t('billing.creditGrants.voided')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {cg.is_active && !cg.is_expired ? (
                        <Button variant="ghost" size="sm" onClick={() => setVoidTarget(cg)}>{t('billing.creditGrants.void')}</Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <GrantCreditModal
        open={grantOpen}
        isSaving={store.isSaving}
        onCancel={() => setGrantOpen(false)}
        onSubmit={handleGrant}
      />

      <VoidCreditGrantModal
        open={!!voidTarget}
        grant={voidTarget}
        isSaving={store.isLoading}
        onCancel={() => setVoidTarget(null)}
        onSubmit={handleVoid}
      />
    </Card>
  );
});

export default CreditGrantsCard;
